
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useFormContext, useWatch } from "react-hook-form";
import { TierFormType } from "../TierEditorUtils";
import { ContentContainer } from "../../../content-container/ContentContainer";
import { ContentSection } from "../../../content-section/ContentSection";
import FormPropertySection from "../../../forms/form-property-section/FormPropertySection";
import FormProperty from "../../../forms/form-property/FormProperty";
import FormInput from "../../../forms/form-input/FormInput";
import { Alert, Stack, Typography, useTheme } from "@mui/material";
import FormRadioGroup, { FormRadioGroupRadioButton } from "../../../forms/form-radio-group/FormRadioGroup";
import { AlarmNotificationTypeEnum } from "../../../../common/enums/AlarmNotificationTypeEnum";
import { isMobile, isTablet } from "react-device-detect";

export default function TierSettings(
) {

    const isPhone = isMobile && !isTablet;
    const { t } = useTranslation();
    const theme = useTheme();

    const {
        getValues,
        watch,
        trigger,
        control,
        //reset,
        setValue,
        formState: {
            errors
        }
        //resetField
    } = useFormContext<TierFormType>();    


    const tier = useWatch({ control: control }) as TierFormType;

    const [
        id,
        tierType,
        name, 
        repeatCountMinutes,
        repeatMinutes,
        repeatZCount,
        tiers
    ] = watch(['id',
        'type',
        'name',
        'repeatCountMinutes',
        'repeatMinutes',
        'repeatZCount',
        'tiers'
    ]);


    const radioButtons: FormRadioGroupRadioButton[] = [{
        label: t('alarmSchemeAdmin.tiers.alarmOnce'),
        ariaLabel: t('alarmSchemeAdmin.tiers.alarmOnce') as string,
        value: AlarmNotificationTypeEnum.Once
        
    }, {
        label: <Stack direction='row'>
                <Typography component='div' 
                    sx={{
                        whiteSpace: 'nowrap',
                        display: 'inline-flex',
                        marginTop: tierType === AlarmNotificationTypeEnum.EveryXMinutes && repeatMinutes === undefined ? 4 : 3,
                        marginBottom: 1
                    }}>
                        Alarm every
                </Typography>

                <FormInput
                    disabled={tierType !== AlarmNotificationTypeEnum.EveryXMinutes}
                    name="repeatMinutes"
                    numeric={true}
                    label='minutes'
                    sx={{
                        width: theme.spacing(isPhone ? 8 : 16),
                        //width: 1,
                        //marginTop: 1,
                        //marginBottom: 1,
                        margin: 1,
                        marginTop: tierType === AlarmNotificationTypeEnum.EveryXMinutes && repeatMinutes === undefined ? 2 : 1,
                    }}
                    rules={{
                        required: {
                            value: tierType === AlarmNotificationTypeEnum.EveryXMinutes,
                            message: t('alarmSchemeAdmin.tiers.required')
                        },
                        //validate: (value: string) => !_validateSetPointName(value) ?
                        //t('setpointAdmin.setpointNameLength') : 
                        //true
                    }}
                    onChange={onRepeatMinutesChanged}

                />

                <Typography component='div'
                    sx={{
                        whiteSpace: 'nowrap',
                        display: 'inline-flex',
                        marginTop: tierType === AlarmNotificationTypeEnum.EveryXMinutes && 
                            repeatMinutes === undefined ? 
                            4 : 
                            3,
                        marginBottom: 1
                    }}>
                    until acknowledged.
                </Typography>

        </Stack>,
        ariaLabel: t('alarmSchemeAdmin.tiers.everyXMinutes') as string,
        value: AlarmNotificationTypeEnum.EveryXMinutes,
    }, {
        label: <Stack direction='row'>
                    <Typography component='div' 
                        sx={{
                            whiteSpace: 'nowrap',
                            display: 'inline-flex',
                            marginTop: tierType === AlarmNotificationTypeEnum.ZTimesYMinutesApart && (repeatZCount === undefined || repeatCountMinutes === undefined) ? 4 : 3,
                            marginBottom: 1
                        }}>
                            Alarm
                    </Typography>

                    <FormInput
                        disabled={tierType !== AlarmNotificationTypeEnum.ZTimesYMinutesApart}
                        name="repeatZCount"
                        numeric={true}
                        label='times'
                        sx={{
                            width: theme.spacing(isPhone ? 8 : 16),
                            //width: 1,
                            //marginTop: 1,
                            //marginBottom: 1,
                            margin: 1,
                            marginTop: tierType === AlarmNotificationTypeEnum.ZTimesYMinutesApart && (repeatZCount === undefined || repeatCountMinutes === undefined) ? 2 : 1,
                        }}
                        rules={{
                            required: {
                                value: tierType === AlarmNotificationTypeEnum.ZTimesYMinutesApart,
                                message: t('alarmSchemeAdmin.tiers.required')
                            },
                            validate: (value: string) => !_validateRepeatZCount(value) ? 
                            '' :
                            //t('alarmSchemeAdmin.tiers.invalidRepeatCount', {count: tier.maxRepeats}) : 
                            true
                        }}
                        onChange={onRepeatZChanged}

                    />

                    <FormInput
                        disabled={tierType !== AlarmNotificationTypeEnum.ZTimesYMinutesApart}
                        name="repeatCountMinutes"
                        numeric={true}
                        label='minutes'
                        sx={{
                            width: theme.spacing(isPhone ? 8 : 16),
                            //width: 1,
                            //marginTop: 1,
                            //marginBottom: 1,
                            margin: 1,
                            marginTop: tierType === AlarmNotificationTypeEnum.ZTimesYMinutesApart && (repeatZCount === undefined || repeatCountMinutes === undefined) ? 2 : 1,
                        }}
                        rules={{
                            required: {
                                value: tierType === AlarmNotificationTypeEnum.ZTimesYMinutesApart,
                                message: t('alarmSchemeAdmin.tiers.required')
                            },
                            //validate: (value: string) => !_validateSetPointName(value) ?
                            //t('setpointAdmin.setpointNameLength') : 
                            //true
                        }}
                        onChange={onRepeatZChanged}

                    />



                    <Typography component='div'
                        sx={{
                            whiteSpace: 'nowrap',
                            display: 'inline-flex',
                            marginTop: tierType === AlarmNotificationTypeEnum.ZTimesYMinutesApart && (repeatZCount === undefined || repeatCountMinutes === undefined) ? 4 : 3,
                            marginBottom: 1
                        }}>
                        {t('alarmSchemeAdmin.tiers.minutesApart')}
                    </Typography>

            </Stack>,
        ariaLabel: t('alarmSchemeAdmin.tiers.zTimes') as string,
        value: AlarmNotificationTypeEnum.ZTimesYMinutesApart
    },
];

    const _validateRepeatZCount = (value: string): boolean => {
        return value === undefined || +value <= tier.maxRepeats;
    }

    function onRepeatMinutesChanged() {

        setValue('repeatZCount', undefined);
        setValue('repeatCountMinutes', undefined);
    }

    function onRepeatZChanged() {

        setValue('repeatMinutes', undefined);
    }

    function onTierTypeChange() {

        const value = getValues('type');

        if (value === AlarmNotificationTypeEnum.Once) {
            setValue('repeatMinutes', undefined);
            setValue('repeatZCount', undefined);
            setValue('repeatCountMinutes', undefined);
        } else if (value === AlarmNotificationTypeEnum.EveryXMinutes) {
            setValue('repeatZCount', undefined);
            setValue('repeatCountMinutes', undefined);
        } else if (value === AlarmNotificationTypeEnum.ZTimesYMinutesApart) {
            setValue('repeatMinutes', undefined);
        }

        trigger();
        
    }

    const _validateNameLength = (value: string): boolean => {
        return value.toString().trim().length <= 50;
    }

    const _validateName = (value: string): boolean => {

        return tiers.find(o => o.id !== id && o.name.toLowerCase() === value.trim().toLowerCase()) === undefined;
    }


    // execute on every render
    useEffect(() => {

        setTimeout(() => {
            let items: ('name' | 'repeatCount' | 'repeatMinutes' | 'repeatZCount' | 'repeatCountMinutes')[] = [];


            if ((name?.trim().length ?? 0) === 0) {
                items.push('name');
            }

            if (items.length) {
                trigger(items);
            }

        }, 1);
    });

   return (

        <ContentContainer>
            <ContentSection sx={{
                padding: 1,
                overflowY: 'auto'
            }}>
            
                <FormPropertySection label={t('properties.settings')}>

                    <FormProperty>
                        <FormInput
                            name="name"
                            label={t('properties.name')}
                            sx={{
                                width: 1,
                                marginTop: 1,
                                marginBottom: 1,
                            }}
                            rules={{
                                required: {
                                    value: true,
                                    message: t('alarmSchemeAdmin.tiers.nameRequired')
                                },
                                validate: (value: string) => !_validateNameLength(value) ?
                                t('alarmSchemeAdmin.tiers.nameLength') : 
                                !_validateName(value) ?
                                t('alarmSchemeAdmin.tiers.nameUnique') : true
                            }}
                        />
                    </FormProperty>

                </FormPropertySection>

                <FormPropertySection label=''>
                <FormProperty>
                        <FormInput
                            name="delayMinutes"
                            numeric={true}
                            label='Minutes after alarm occurs before tier will begin'
                            sx={{
                                //width: theme.spacing(4),
                                width: 1,
                                marginTop: 1,
                                marginBottom: 1,
                            }}
                            rules={{
                                required: {
                                    value: true,
                                    message: t('alarmSchemeAdmin.tiers.delayMinutesRequired')
                                },
                                //validate: (value: string) => !_validateSetPointName(value) ?
                                //t('setpointAdmin.setpointNameLength') : 
                                //true
                            }}
                        />

                    </FormProperty>
                    </FormPropertySection>                    

                    
                    <FormRadioGroup label=''
                        name='type'
                        radioButtons={radioButtons} 
                        onChange={onTierTypeChange}/>

                    <Alert severity={errors.repeatZCount ? 'error' : 'info'}
                        sx={{
                            //marginTop:'auto',
                            marginTop: 1
                        }}>
                        {t('alarmSchemeAdmin.tiers.maxRepeats', {
                            count: tier.maxRepeats
                        })}
                    </Alert>

            </ContentSection>        
        </ContentContainer>
        
    );
  }
